import {ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {merge} from 'rxjs';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {PasswordFormFieldComponent} from '@aviary/forms/fields/password-form-field/password-form-field.component';
import {buildFormGroup, getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {InputMode} from '@aviary/forms/fields/form-fields.types';

interface CredentialConfirmationValue {
  confirmation: string;
}

@Component({
  selector: 'mx-confirmed-credential-form-field',
  template: `
    <mx-password-form-field [fieldName]="fieldName" [overrideLabel]="credentialLabel" [includeClearButton]="false" [formGroup]="formGroup"
                            [maxLength]="maxLength" [inputMode]="inputMode" [showValidationErrors]="showCredentialValidationErrors"/>

    <div class="empty:h-5">
      <ng-content select="[credentialRequirements]"/>
    </div>

    <mx-password-form-field fieldName="confirmation" [overrideLabel]="confirmationLabel" [includeClearButton]="false"
                            [formGroup]="confirmationFormGroup" [maxLength]="maxLength" [inputMode]="inputMode"/>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PasswordFormFieldComponent]
})
export class ConfirmedCredentialFormFieldComponent implements OnInit, OnDestroy {
  @Input({required: true}) public fieldName: string;
  @Input({required: true}) public formGroup: ExtendedFormGroup;
  @Input() public credentialLabel: string = 'Credential';
  @Input() public confirmationLabel: string = 'Confirm Credential';
  @Input() public mismatchErrorMessage: string = 'Credentials do not match.';
  @Input() public maxLength?: number;
  @Input() public inputMode?: InputMode;
  @Input() public showCredentialValidationErrors: boolean = true;

  public readonly confirmationFormGroup: ExtendedFormGroup = buildFormGroup<CredentialConfirmationValue>(
    {confirmation: ''},
    {confirmation: [Validators.required]}
  );

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly confirmationControl: ExtendedFormControl = getExtendedControlFrom(this.confirmationFormGroup, 'confirmation');
  private readonly confirmationValidator: ValidatorFn = (): ValidationErrors | null =>
    this.confirmationControl.valid ? null : {credentialConfirmation: true};
  private readonly matchingValidator: ValidatorFn = (): ValidationErrors | null => {
    const credential: string = this.credentialControl.value ?? '';
    const confirmation: string = this.confirmationControl.value ?? '';

    return confirmation.length > 0 && credential !== confirmation ? {credentialMismatch: this.mismatchErrorMessage} : null;
  };
  private credentialControl: ExtendedFormControl;

  public ngOnInit(): void {
    this.credentialControl = getExtendedControlFrom(this.formGroup, this.fieldName);
    if (this.maxLength !== undefined) this.confirmationControl.addValidators(Validators.maxLength(this.maxLength));
    this.confirmationControl.addValidators(this.matchingValidator);
    this.formGroup.addValidators(this.confirmationValidator);

    merge(this.credentialControl.valueChanges, this.confirmationControl.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.confirmationControl.updateValueAndValidity({emitEvent: false});
        this.formGroup.updateValueAndValidity({emitEvent: false});
      });

    this.confirmationControl.updateValueAndValidity({emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
  }

  public ngOnDestroy(): void {
    this.formGroup.removeValidators(this.confirmationValidator);
    this.formGroup.updateValueAndValidity({emitEvent: false});
  }
}
